import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import styled from 'styled-components';
import { wiki, c, WikiContainer, WikiThemeProvider, useWikiTheme, COLOR_PRESETS } from './wikiStyles';
import { useWikiConfig } from './useWiki';
import WikiIndex from './WikiIndex';
import WikiPage from './WikiPage';
import WikiNotFound from './WikiNotFound';

/* ── Header ─────────────────────────────────────────────── */

const AccentBar = styled.div`
  height: 3px;
  background: linear-gradient(
    90deg,
    ${c.accent} 0%,
    color-mix(in srgb, ${c.accent} 56%, transparent) 25%,
    color-mix(in srgb, ${c.accent} 31%, transparent) 50%,
    color-mix(in srgb, ${c.accent} 8%, transparent) 75%,
    transparent 100%
  );
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${c.border};
  backdrop-filter: blur(16px);
  background: ${c.headerBg};
  box-shadow: 0 1px 12px ${c.shadow}, 0 4px 24px ${c.shadow};
`;

const HeaderInner = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
  padding: 0 2.5rem;
  height: ${wiki.headerHeight};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1080px) {
    padding: 0 2rem;
  }
  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
`;

const LogoMark = styled.span`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${c.accent};
  color: ${c.bgWhite};
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 6px;
`;

const LogoTitle = styled.span`
  font-family: ${wiki.fonts.heading};
  font-size: 1.15rem;
  font-weight: 600;
  color: ${c.text};
  letter-spacing: -0.01em;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PoweredBy = styled.span`
  font-family: ${wiki.fonts.ui};
  font-size: 0.72rem;
  color: ${c.textMuted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  @media (max-width: 600px) {
    display: none;
  }
`;

const DharaLogo = styled.img`
  height: 18px;
  width: auto;
  vertical-align: middle;
  opacity: 0.85;
`;

const ThemeToggle = styled.button`
  background: none;
  border: 1px solid ${c.border};
  color: ${c.textMuted};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;

  &:hover {
    color: ${c.accent};
    border-color: ${c.accent};
  }
`;

/* ── Color Picker ────────────────────────────────────────── */

const PickerWrap = styled.div`
  position: relative;
`;

const PickerToggle = styled.button`
  background: none;
  border: 1px solid ${c.border};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    border-color: ${c.accent};
    transform: scale(1.1);
  }
`;

const PickerSwatch = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${c.accent};
  display: block;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1);
`;

const PickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${c.bgWhite};
  border: 1px solid ${c.border};
  border-radius: ${wiki.radius};
  box-shadow: 0 8px 32px ${c.shadowMd};
  padding: 0.85rem 1rem;
  z-index: 200;
  box-sizing: border-box;
  user-select: none;
`;

const PickerSectionLabel = styled.div`
  font-family: ${wiki.fonts.ui};
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${c.textMuted};
  margin-bottom: 0.5rem;
`;


const PickerPresets = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PresetDot = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2.5px solid ${(p) => (p.$active ? c.text : 'transparent')};
  background: ${(p) => p.$color};
  cursor: pointer;
  padding: 0;
  transition: all 0.15s;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.08);
  outline: ${(p) => (p.$active ? `2px solid ${c.bgWhite}` : 'none')};
  outline-offset: -4px;

  &:hover {
    transform: scale(1.15);
    border-color: ${c.textMuted};
  }
`;


const SiteLink = styled.a`
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  color: ${c.textSec};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid ${c.border};
  border-radius: ${wiki.radiusSm};
  transition: all 0.2s;

  &:hover {
    color: ${c.accent};
    border-color: ${c.accent};
  }
`;

/* ── Footer ─────────────────────────────────────────────── */

const FooterDivider = styled.div`
  max-width: 80px;
  height: 1px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(to right, transparent, ${c.border}, transparent);
  position: relative;

  &::after {
    content: '\\25C7';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${c.bg};
    padding: 0 0.5rem;
    color: ${c.accent};
    font-size: 0.55rem;
  }
`;

const Footer = styled.footer`
  border-top: 1px solid ${c.border};
  padding: 3rem 2rem 2.5rem;
  text-align: center;
  margin-top: 4rem;
`;

const FooterInner = styled.div`
  max-width: ${wiki.maxPage};
  margin: 0 auto;
`;

const FooterLine = styled.p`
  font-family: ${wiki.fonts.ui};
  font-size: 0.78rem;
  color: ${c.textMuted};
  line-height: 1.7;

  a {
    color: ${c.accent};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    &:hover {
      text-decoration: underline;
    }
  }

  & + & {
    margin-top: 0.35rem;
  }
`;

/* ── Color Picker component ─────────────────────────────── */

function ColorPickerUI() {
  const { accentColor, setAccentColor } = useWikiTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <PickerWrap ref={wrapRef}>
      <PickerToggle
        onClick={() => setOpen((v) => !v)}
        title="Change theme color"
      >
        <PickerSwatch />
      </PickerToggle>
      {open && (
        <PickerDropdown>
          <PickerSectionLabel>Choose Theme</PickerSectionLabel>
          <PickerPresets>
            {COLOR_PRESETS.map((p) => (
              <PresetDot
                key={p.hex}
                $color={p.hex}
                $active={accentColor.toUpperCase() === p.hex.toUpperCase()}
                title={p.name}
                onClick={() => {
                  setAccentColor(p.hex);
                  setOpen(false);
                }}
              />
            ))}
          </PickerPresets>
        </PickerDropdown>
      )}
    </PickerWrap>
  );
}

/* ── Inner layout (needs theme context) ─────────────────── */

function WikiLayoutInner() {
  const { config } = useWikiConfig();
  const { mode, toggleMode } = useWikiTheme();
  const dharaLogo = config.dharaLogoUrl || '/Dhara_logo.png';

  return (
    <WikiContainer>
      <AccentBar />
      <Header>
        <HeaderInner>
          <LogoLink to={config.wikiBasePath || '/wiki'}>
            <LogoMark>&#9671;</LogoMark>
            <LogoTitle>
              {config.siteTitle || 'Indic Knowledge Encyclopedia'}
            </LogoTitle>
          </LogoLink>

          <HeaderRight>
            <PoweredBy>
              Powered by <DharaLogo src={dharaLogo} alt="Dhara" />
            </PoweredBy>
            <ColorPickerUI />
            <ThemeToggle
              onClick={toggleMode}
              title={
                mode === 'light'
                  ? 'Switch to dark mode'
                  : 'Switch to light mode'
              }
            >
              {mode === 'light' ? '☾' : '☀'}
            </ThemeToggle>
            <SiteLink href={config.mainSiteUrl || '/'}>
              &larr; {config.brandName || 'Home'}
            </SiteLink>
          </HeaderRight>
        </HeaderInner>
      </Header>

      <main>
        <Routes>
          <Route index element={<WikiIndex />} />
          <Route path=":slug/*" element={<WikiPage />} />
          <Route path="*" element={<WikiNotFound />} />
        </Routes>
      </main>

      <Footer>
        <FooterInner>
          <FooterDivider />
          <FooterLine>{config.footerText || ''}</FooterLine>
          <FooterLine>
            <a href={config.poweredByUrl || '#'}>
              Powered by{' '}
              <DharaLogo
                src={dharaLogo}
                alt="Dhara"
                style={{ height: '16px' }}
              />
            </a>
          </FooterLine>
        </FooterInner>
      </Footer>
    </WikiContainer>
  );
}

/* ── Root export (provides theme) ───────────────────────── */

export default function WikiLayout() {
  const { config } = useWikiConfig();

  return (
    <WikiThemeProvider primaryColor={config.primaryColor}>
      <WikiLayoutInner />
    </WikiThemeProvider>
  );
}
